import { useMemo } from 'react';
import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { get, set } from 'lodash';
import { buildTimeseriesQuery, buildGroupByQuery, formatQueryStatus } from './utils';
import { getDimensionData } from '@api/dimensionContainer';
import {
  filterSelector,
  realTimeModeOnTrendsSelector,
  getFirstAppliedFilter,
} from '@store/common/selectors';
import getGranularity from '@utils/granularity';
import instance from '@api/axiosInstance';
import { getErrorCodeFilter, NETWORK_REQUEST_RESPONSE_CODE_DIMENSION } from '../constants';
import { trackQueryError } from '@utils/sensor/apptracker';
import { DATE_SHORTCUTS, isAppModule } from '@utils/constants';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';

export function useStackedGroupByHooks({ interval, metric, enabled = true }: any) {
  const filter = useSelector(filterSelector);
  const realTimeOnTrends: boolean = useSelector(realTimeModeOnTrendsSelector);
  const groupByQuery = useMemo(() => {
    const q = buildGroupByQuery({
      dimension: NETWORK_REQUEST_RESPONSE_CODE_DIMENSION,
      metrics: [metric],
      filter,
      interval: `${moment(interval.from).toISOString()}/${moment(interval.to).toISOString()}`,
    }) as any;
    q.isLiveMode = realTimeOnTrends;
    if (realTimeOnTrends) {
      q.ignoreInterval = true;
    }
    set(q, ['options', 'withTotals'], false);
    return q;
  }, [metric, filter, interval, realTimeOnTrends]);

  const {
    data = {},
    refetch,
    isFetching,
  } = useQuery(
    ['app-network-groupby', groupByQuery],
    () => getDimensionData({ queries: [groupByQuery] }, {}, instance),
    {
      enabled,
      refetchOnWindowFocus: false,
      keepPreviousData: realTimeOnTrends,
      select: (response) => {
        const { data = [], totals = {} } = get(response, ['data', 0], {});
        return { data, totals };
      },
      retry: 1,
      onError: (err) => {
        trackQueryError({ type: 'app-network-groupby', err });
      },
    },
  );

  const errorCodeList = useMemo(() => {
    const errorCodes = get(data, ['data', '0', 'results'], []);
    if (errorCodes.length > 0) {
      const limitedErrorCodes = errorCodes
        .filter(
          (eachCode) =>
            !['UNKNOWN'].includes(eachCode[NETWORK_REQUEST_RESPONSE_CODE_DIMENSION]) &&
            !isNaN(eachCode[metric]),
        )
        .map((code) => {
          return code[NETWORK_REQUEST_RESPONSE_CODE_DIMENSION];
        });
      return limitedErrorCodes.splice(0, 5);
    }
  }, [data, metric]);

  const networkFilterList = useMemo(() => {
    return getErrorCodeFilter(errorCodeList);
  }, [errorCodeList]);

  return {
    isLoading: isFetching,
    filterList: networkFilterList,
    refetch,
  };
}

export function useStackedDataHooks({
  metrics,
  interval,
  filters,
  enabled = true,
  keepPreviousData,
}: any) {
  const globalFilter = useSelector(filterSelector);
  const { usecaseKey, dataScope }: any = useSelector(activeDashboardSelector);
  const realTimeOnTrends: boolean = useSelector(realTimeModeOnTrendsSelector);
  const granularity = getGranularity({
    from: interval.from,
    to: interval.to,
    minuteGranularity: true,
    overview: false,
    shortcut: realTimeOnTrends && (DATE_SHORTCUTS.last15Mins as any),
  });

  const filter = filters.length ? [...globalFilter, filters] : globalFilter;

  const query: any = buildTimeseriesQuery({
    metrics,
    filter,
    interval: `${moment(interval.from).format()}/${moment(interval.to).format()}`,
    granularity,
    isLiveMode: realTimeOnTrends,
  });
  if (isAppModule) {
    query.options.dataScope = dataScope;
    query.options.presetKey = usecaseKey;
    query.options.withTotals = false;
  }
  query.groupBy = [NETWORK_REQUEST_RESPONSE_CODE_DIMENSION];
  query.options.stacked = { metric: NETWORK_REQUEST_RESPONSE_CODE_DIMENSION };
  const { data, ...queryStatus } = useQuery(
    ['app-network-code', query],
    () => {
      return getDimensionData({ queries: [query] }, {}, instance).then((response) =>
        get(response, ['data', 0, 'data'], []),
      );
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      retry: false,
      enabled,
      keepPreviousData,
      onError: (err) => {
        trackQueryError({ type: 'app-network-code', err });
      },
    },
  );

  return {
    ...formatQueryStatus({ ...queryStatus, isEmpty: !(data as any)?.length }),
    data,
    interval,
  };
}

export function useStackedDurationHooks({
  interval,
  dimension,
  metric,
  keepPreviousData,
  enabled = true,
}) {
  const globalFilter = useSelector(getFirstAppliedFilter);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);

  const granularity = getGranularity({
    from: interval.from,
    to: interval.to,
    minuteGranularity: true,
    overview: false,
    shortcut: realTimeOnTrends && (DATE_SHORTCUTS.last15Mins as any),
  });
  const { usecaseKey, dataScope }: any = useSelector(activeDashboardSelector);
  const query: any = useMemo(() => {
    const filter = [...(globalFilter.rule || [])];
    const q = buildTimeseriesQuery({
      metrics: [metric, dimension],
      filter,
      interval: `${moment(interval.from).format()}/${moment(interval.to).format()}`,
      granularity,
      isLiveMode: realTimeOnTrends,
    }) as any;
    q.options.stacked = { metric: dimension };
    if (isAppModule) {
      q.options.dataScope = dataScope;
      q.options.presetKey = usecaseKey;
      q.options.withTotals = false;
    }
    return q;
  }, [
    dimension,
    globalFilter.rule,
    granularity,
    interval.from,
    interval.to,
    metric,
    realTimeOnTrends,
    usecaseKey,
    dataScope,
  ]);

  const { data, ...queryStatus } = useQuery(
    ['app-network-duration-timeseries', query],
    () => {
      return getDimensionData({ queries: [query] }, {}, instance).then((response) => {
        return get(response, ['data', 0, 'data'], []);
      });
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      retry: false,
      enabled,
      keepPreviousData,
    },
  );

  return {
    ...formatQueryStatus({ ...queryStatus, isEmpty: !(data as any)?.length }),
    data,
  };
}
